import { cn } from '@/lib/utils';
import { useManualSession } from '@/store/manual';
import { useMapMode } from '@/store/mapMode';

/**
 * Manual-mode overlay — dead-man state + live stick values, floating at the
 * bottom of the map. Only mounts in Manual mode; deliberately tiny so the
 * map stays the star. The keymap itself lives in the mode strip up top.
 */
export function ManualOverlay() {
  const mode = useMapMode((s) => s.mode);
  const engaged = useManualSession((s) => s.engaged);
  const sticks = useManualSession((s) => s.sticks);

  if (mode !== 'manual') return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex justify-center px-3">
      <div
        className={cn(
          'flex items-center gap-3 rounded-sm border bg-base/85 px-3 py-1.5 font-mono text-[10px] shadow-[var(--shadow-pop)] backdrop-blur-md',
          engaged ? 'border-status-good/60' : 'border-border-default',
        )}
      >
        <span
          className={cn(
            'font-semibold uppercase tracking-[0.14em]',
            engaged ? 'text-status-good' : 'text-fg-muted',
          )}
        >
          {engaged ? 'ENGAGED' : 'HOLD SPACE'}
        </span>
        <span className="h-3 w-px bg-border-default" aria-hidden />
        <Stick label="PIT" value={sticks.pitch} />
        <Stick label="ROL" value={sticks.roll} />
        <Stick label="YAW" value={sticks.yaw} />
        <Stick label="CLB" value={sticks.throttle} />
      </div>
    </div>
  );
}

function Stick({ label, value }: { label: string; value: number }) {
  const pct = Math.max(-1, Math.min(1, value)) * 50;
  return (
    <span className="flex items-center gap-1">
      <span className="uppercase tracking-[0.08em] text-fg-muted">{label}</span>
      <span className="relative h-1.5 w-10 overflow-hidden rounded-full bg-surface-2">
        <span className="absolute inset-y-0 left-1/2 w-px bg-border-default" aria-hidden />
        <span
          className="absolute inset-y-0 bg-accent/80"
          style={
            pct >= 0
              ? { left: '50%', width: `${pct}%` }
              : { right: '50%', width: `${-pct}%` }
          }
        />
      </span>
    </span>
  );
}
